import { useCallback, useEffect, useState, useMemo } from "react";
import ChartByTime, { TimeRangeConfig } from "components/dashboard/ChartByTime";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import { ChartAttribute } from "generated/models";
import moment from "moment";
import styled from "@emotion/styled";

import styledConst from "styles";
import { ConsumptionChartBy } from "types";
import WidgetBox from "../WidgetBox";
import WidgetContent from "../WidgetContent";
import { compareNumber } from "utils/functions";
import { fetchMeteringInfo } from "./TokenStackedChart";
import _ from "lodash";

type ConsumptionChartProps = Readonly<
    Record<string, any> & {
        panel: Record<string, any> & {
            query_time: number | null;
            time_range: string;
            attributes: ChartAttribute[];
            uuid: string;
            name: string;
        };
    }
>;

type SeriesData = {
    sms: number;
    dashboard: number;
    notification: number;
    email: number;
    offload: number;
    total: number;
    previousTotal: number;
};

const PercentageBreakdownWrapper = styled.div`
    position: absolute;
    top: 3px;
    left: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 14px;

    .total {
        font-size: 24px;
        color: ${styledConst.Primary_Green_1};
        font-weight: 500;
    }

    .muted {
        color: #828fab;
    }

    .increase-icon-wrapper {
        color: ${styledConst.Secondary_Red};
    }

    .decrease-icon-wrapper {
        color: ${styledConst.Primary_Green_1};
    }

    .diff-trend-wrapper {
        display: flex;
        gap: 5px;
        height: 16px;
    }

    .diff-trend-value {
        color: #fff;
    }
`;

const FilterWrapper = styled.div`
    position: absolute;
    right: 0;
    top: 12px;
    z-index: 1;
`;

const StyledWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

const PercentageBreakdown = ({
    previous,
    current,
    groupBy,
}: {
    previous: number | null;
    current: number | null;
    groupBy: ConsumptionChartBy;
}) => {
    const percent = compareNumber(current, previous);

    return (
        <PercentageBreakdownWrapper>
            <div className="muted">Percentage Breakdown</div>
            <div style={{ display: "flex", gap: 5 }}>
                <div className="total">{current ?? "-"}</div>
                <div className="muted">
                    (Total for the{" "}
                    {groupBy === ConsumptionChartBy.Year ? "year" : "month"})
                </div>
            </div>
            <div className="diff-trend-wrapper">
                {percent && (
                    <>
                        <div className="diff-trend-value">
                            {percent.direction === "decrease" ? (
                                <span className="decrease-icon-wrapper">↓</span>
                            ) : (
                                <span className="increase-icon-wrapper">↑</span>
                            )}{" "}
                            {percent.percentageToShow}%
                        </div>
                        <div className="muted">
                            {percent.direction === "decrease"
                                ? "decrease"
                                : "increase"}{" "}
                            from previous{" "}
                            {groupBy === ConsumptionChartBy.Year
                                ? "year"
                                : "month"}
                        </div>
                    </>
                )}
            </div>
        </PercentageBreakdownWrapper>
    );
};

const TokenDonutChart = (props: ConsumptionChartProps) => {
    const { panel, isVisible, visibleRef } = props;
    const { name = "", attributes } = panel;
    const organizationId = _.get(attributes, "[0].organization_id", "");

    const [isLoading, setIsLoading] = useState(true);
    const [groupBy, setGroupBy] = useState(ConsumptionChartBy.Month);
    const [timeRange, setTimeRange] = useState<TimeRangeConfig>({
        timeRange: "", // Pass empty string to by pass ts validation, not used in this chart
        lastTime: moment(),
    });
    const [tokenData, setTokenData] = useState<SeriesData | null>(null);

    const fetchNewData = useCallback(async () => {
        try {
            // Get year and month from the selected timeRange
            const currentYear = timeRange.lastTime.year();
            const currentMonth = timeRange.lastTime.month() + 1; // moment months are 0-indexed

            // Calculate previous period based on groupBy
            const isYearView = groupBy === ConsumptionChartBy.Year;

            const meteringInfo = await fetchMeteringInfo({
                year: currentYear,
                month: isYearView ? undefined : currentMonth,
                organizationId,
            });

            if (!meteringInfo) {
                return;
            }

            let currentTotal = 0;
            let previousTotal = 0;

            let email = 0;
            let offload = 0;
            let sms = 0;
            let dashboard = 0;
            let notification = 0;

            if (isYearView) {
                const data = meteringInfo.yearData;
                currentTotal = meteringInfo.yearData.currYearTotal;
                previousTotal = meteringInfo.yearData.prevYearTotal;

                email = data.currYearCounts.email;
                offload = data.currYearCounts.offload;
                sms = data.currYearCounts.sms;
                dashboard = data.currYearCounts.dashboard;
                notification = data.currYearCounts.notification;
            } else {
                const data = meteringInfo.monthData;

                currentTotal = Object.values(data.currMonthCounts).reduce(
                    (sum, val) => sum + val,
                    0
                );
                previousTotal = Object.values(data.prevMonthCounts).reduce(
                    (sum, val) => sum + val,
                    0
                );

                email = data.currMonthCounts.email;
                offload = data.currMonthCounts.offload;
                sms = data.currMonthCounts.sms;
                dashboard = data.currMonthCounts.dashboard;
                notification = data.currMonthCounts.notification;
            }

            setTokenData({
                sms,
                dashboard,
                notification,
                email,
                offload,
                total: currentTotal,
                previousTotal: previousTotal,
            });
        } finally {
            setIsLoading(false);
        }
    }, [groupBy, timeRange, organizationId]);

    useEffect(() => {
        if (!isVisible) {
            return;
        }

        fetchNewData();
    }, [isVisible, fetchNewData]);

    const onTimeChange = async (
        timeRange: TimeRangeConfig,
        groupBy: ConsumptionChartBy
    ) => {
        setTimeRange(timeRange);
        setGroupBy(groupBy);
    };

    const option: EChartsOption = useMemo(() => {
        const legends: [string, keyof SeriesData][] = [
            ["Email", "email"],
            ["SMS", "sms"],
            ["Notification", "notification"],
            ["Dashboard Data", "dashboard"],
            ["Data Download", "offload"],
        ];

        // Pre-calculate percentages so they always sum to exactly 100%
        // (last item = 100 - sum of all others), matching StorageChart's approach
        const total = tokenData?.total ?? 0;
        const percentages: number[] = legends.map(() => 0);
        if (total > 0 && tokenData) {
            let sumSoFar = 0;
            for (let i = 0; i < legends.length - 1; i++) {
                const pct = Number(
                    (
                        ((tokenData[legends[i][1]]) / total) *
                        100
                    ).toFixed(1)
                );
                percentages[i] = pct;
                sumSoFar += pct;
            }
            percentages[legends.length - 1] = Number(
                (100 - sumSoFar).toFixed(1)
            );
        }

        return {
            backgroundColor: "#2f3a51",
            color: ["#EE9EC6", "#F6C39D", "#FFEFB5", "#A2DFD8", "#52BFD4"],
            responsive: true,
            maintainAspectRatio: false,
            tooltip: {
                trigger: "item",
            },

            legend: {
                selectedMode: true,
                bottom: 0,
                left: 0,
                right: 0,
                /** 
                 * Without this, legends will be the serie name ("Token Spending") when the data is empty, this is not ideal
                 * Just be used for the empty case even it would show nothing
                 **/
                data: legends.map((item) => item[0]),
                textStyle: {
                    color: "#ffffff",
                    fontFamily: "Roboto",
                    fontSize: 14,
                    fontWeight: 400,
                },
                padding: 0,
                itemGap: 20,
                itemWidth: 6,
                itemHeight: 6,
                icon: "circle",
                type: "scroll",
                pageIconColor: "#c8c6c6",
                pageIconInactiveColor: "#6c6d6e",
                pageTextStyle: {
                    color: "#ffffff",
                },
            },
            series: [
                {
                    name: "Token Spending",
                    type: "pie",
                    radius: ["55%", "85%"],
                    top: 70,
                    bottom: 20,
                    label: {
                        position: "inside",
                        formatter: (params: any) => {
                            const pct = percentages[params.dataIndex];
                            return pct > 0 ? `${pct}%` : "";
                        },
                        fontSize: 9,
                        fontWeight: 700,
                        fontColor: "#474747",
                        show: !!tokenData?.total, // Hide the labels because we hide the pie when total is zero
                    },
                    labelLine: {
                        show: false,
                        length: 8,
                        length2: 8,
                        lineStyle: { color: "#828fab" },
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowColor: "rgba(0, 0, 0, 0.5)",
                        },
                    },
                    showEmptyCircle: true,
                    emptyCircleStyle: {
                        color: "#828fab",
                    },
                    data: (tokenData && total > 0)
                        ? legends.map(([label, key], i) => {
                            let extraProps = {};
                            if (percentages[i] === 0) {
                                extraProps = { label: { show: false } };
                            } else if (percentages[i] < 5) {
                                extraProps = {
                                    label: { position: "outside", distanceToLabelLine: 2 },
                                    labelLine: { show: !!tokenData?.total },
                                };
                            }
                            return {
                                value: tokenData[key] || 0,
                                name: label,
                                ...extraProps,
                            };
                        })
                        : [],
                },
            ],
        };
    }, [tokenData]);

    return (
        <WidgetBox ref={visibleRef} className="widget-box w-100">
            <WidgetContent title={name || "Token Spending"} loading={isLoading}>
                <StyledWrapper>
                    <FilterWrapper>
                        <ChartByTime
                            timeRange={timeRange}
                            onChange={onTimeChange}
                            view={ConsumptionChartBy.Month}
                            validViews={[
                                ConsumptionChartBy.Month,
                                ConsumptionChartBy.Year,
                            ]}
                        />
                    </FilterWrapper>
                    <PercentageBreakdown
                        groupBy={groupBy}
                        previous={tokenData?.previousTotal ?? null}
                        current={tokenData?.total ?? null}
                    />
                    <div style={{ height: "100%", width: "100%" }}>
                        <ReactECharts
                            style={{ height: "100%", width: "100%" }}
                            theme={"dark"}
                            option={option}
                        />
                    </div>
                </StyledWrapper>
            </WidgetContent>
        </WidgetBox>
    );
};

export default TokenDonutChart;
